import { useEffect, useState } from "react";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo } from "../../components/ui/primitivos";
import { fetchConfiguracion, guardarConfiguracion } from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";

/**
 * Datos de la imprenta — porteada de `OrganizationSettings.tsx` (ADR 0043).
 *
 * Allá la pantalla tiene además logo, y el modelo detrás lleva `theme_config`,
 * `is_active`, `name` y `slug`. Nada de eso llega acá: son maquinaria
 * multi-tenant que ADR 0002 descartó. Lo que queda es la identidad fiscal de
 * Red Marketing, que sí es dato del negocio.
 *
 * Se dibuja desde lo que devuelve el endpoint y no desde una lista propia: el
 * worker es el dueño del catálogo de claves, así que agregar una allá la hace
 * aparecer acá sin tocar esta pantalla.
 */
export function ConfiguracionPagina() {
  const { can } = useSession();
  const datos = useDatos(fetchConfiguracion);
  const puedeEditar = can("sistema", "config.update");

  const [valores, setValores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  // Siembra el formulario con lo que vino del servidor.
  //
  // Depende de `datos.datos`, que cambia de identidad SOLO al recargar — al
  // montar y despues de guardar. En los dos casos re-sembrar es lo correcto: no
  // hay edicion en curso que pisar.
  useEffect(() => {
    if (!datos.datos) return;
    setValores(Object.fromEntries(datos.datos.map((d) => [d.clave, d.valor])));
  }, [datos.datos]);

  const sinCambios = (datos.datos ?? []).every((d) => (valores[d.clave] ?? "") === d.valor);

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Datos de la imprenta"
        subtitulo="Los datos de Red Marketing que van en los documentos"
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {datos.error ? <Aviso>{datos.error}</Aviso> : null}
      {listo ? <Aviso tono="ok">Datos actualizados.</Aviso> : null}

      <Card className="space-y-4 p-4">
        {datos.cargando && !datos.datos ? (
          <p className="text-neutral-500 text-sm">Cargando...</p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {(datos.datos ?? []).map((d) => (
            <div key={d.clave} className={d.clave.endsWith("direccion") ? "sm:col-span-2" : ""}>
              <Campo
                etiqueta={d.etiqueta}
                value={valores[d.clave] ?? ""}
                disabled={!puedeEditar}
                onChange={(e) => {
                  setListo(false);
                  setValores((v) => ({ ...v, [d.clave]: e.target.value }));
                }}
              />
            </div>
          ))}
        </div>

        {/* Ocultar no es autorizar (invariante 5): el endpoint exige
            `sistema.config.update` igual. Esto solo evita el 403. */}
        {puedeEditar ? (
          <div className="flex justify-end">
            <Boton
              disabled={guardando || sinCambios}
              onClick={async () => {
                setGuardando(true);
                setError(null);
                try {
                  // Solo lo que cambio: mandar las tres claves siempre dejaria
                  // un evento de auditoria que dice que se toco todo.
                  const cambios = Object.fromEntries(
                    (datos.datos ?? [])
                      .filter((d) => (valores[d.clave] ?? "") !== d.valor)
                      .map((d) => [d.clave, valores[d.clave] ?? ""]),
                  );
                  await guardarConfiguracion(cambios);
                  await datos.recargar();
                  setListo(true);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "No se pudo guardar");
                } finally {
                  setGuardando(false);
                }
              }}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Boton>
          </div>
        ) : null}
      </Card>

      <p className="text-neutral-500 text-sm">
        Todavía no hay documento impreso que los use: el presupuesto en PDF no está construido.
        Estos datos se cargan ahora para que estén cuando exista.
      </p>
    </div>
  );
}
